<template>
    <div>
        <vue-resizable
            v-if="mode === 'table'"
            class="resizable"
            :class="{
                show: displayPanel,
                hidden: hidePanel,
                'has-context-tab': pointer,
                'small-device': showTableViewSmallDevices
            }"
            :active="handlers"
            :width="width"
            :height="height"
            :left="left"
            :top="top"
            :max-width="maxWidth"
            :max-height="maxHeight"
            :min-height="minHeight"
            :min-width="minWidth"
            @resize:end="resizeHandler"
        >
            <div class="shadow information-panel">
                <div
                    class="object-overview-button large-only"
                    :class="{
                        disabled: informationPanel.disabled,
                        'small-device': showTableViewSmallDevices
                    }"
                    v-show="informationPanel.components.overviewButton.visible"
                >
                    <div class="introJsStep10">
                        <!-- Show/hide button -->
                        <div
                            id="object_overview_icon"
                            class="up-white-icon float-right"
                            :class="{
                                rotate_180: informationPanel.focus,
                                disabled: informationPanel.disabled
                            }"
                            :title="
                                informationPanel.focus
                                    ? $t('tableHide')
                                    : $t('tableShow')
                            "
                            @click="
                                $store.commit(
                                    'NEDGLOBE_MOBILE_TOGGLE_FOCUS_INFOPANEL'
                                )
                            "
                        ></div>
                        <!-- Refresh area button -->
                        <div
                            id="table_refresh_icon"
                            v-if="hasRefesh"
                            class="rotate-white-icon float-right table-icon"
                            :title="$t('refreshTable')"
                            @click="$store.dispatch('refreshSelection')"
                        ></div>
                        <!-- Maximize horizontal button -->
                        <!-- Do not show on small devices, because width is already maximized -->
                        <div
                            v-if="displayPanel && !isSmallDeviceLocal"
                            class="arrow-min-max float-left mr-1"
                            :class="{
                                max: !maximizeHorizontal,
                                min: maximizeHorizontal
                            }"
                            @click="$_setMaximizeHorizontal"
                            :title="$t('tableMaximizeHorizontal')"
                        ></div>
                        {{ resultCount }} item{{ resultCount != 1 ? 's' : '' }}
                    </div>
                </div>
                <information-tabs
                    v-if="hasTabs"
                    ref="informationtabs"
                    class="information-tab-content"
                    :orderedTabs="getOrderedTabs"
                    :groups="groups"
                    :objects="objectList"
                    @openBrkDetails="$_openBrkModal"
                    @openStufDetails="$_openStufDetails"
                    @openDetails="$_openDetails"
                    @openKvkDetails="$_openKvkDetails"
                    @openEnergyLabel="$_openEnergyLabel"
                    @openWozData="$_openWozData"
                />
            </div>
        </vue-resizable>
        <div v-if="mode === 'details'">
            <!-- Accessibility details -->
            <detail-panel
                :objects="objectList"
                :selectedObject="getSelectionObjectsSelectedItem"
                :mode="mode"
                :active="true"
            />
        </div>
        <div v-else-if="mode === 'accessible'">
            <div v-if="informationPanel.loading" class="tab-wait large-only">
                <a>{{ $t('loading') }}</a>
            </div>
            <information-list
                v-else
                :groups="groups"
                :objects="objectList"
                @openBrkDetails="$_openBrkModal"
                @openStufDetails="$_openStufDetails"
                @openKvkDetails="$_openKvkDetails"
            />
        </div>
    </div>
</template>

<script>
    import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
    import VueResizable from 'vue-resizable';
    import { createStufBgQueryObject } from '../../../../services/utilities/apis/stuf';
    import { createKvkQueryObject } from '../../../../services/utilities/apis/kvk';
    import { isSmallDevice } from '../../../../services/utilities/devices';
    import {
        MENUBAR_HEIGHT,
        INTERFACE_PANEL_TRIGGERS,
        WOZ_FIELDS
    } from '../../../../constants';
    import informationTabs from './tabs.vue';
    import informationlist from './list.vue';
    import ObjectPanel from './ObjectPanel.vue';

    export default {
        name: 'InformationOverviewTab',
        props: {
            mode: { type: String, required: false, default: 'table' },
            selectedObject: { type: Object, required: false }
        },
        components: {
            'information-tabs': informationTabs,
            'information-list': informationlist,
            'detail-panel': ObjectPanel,
            VueResizable
        },
        data() {
            return {
                handlers: ['t', 'l', 'lt'],
                left: 0,
                top: 0,
                height: 0,
                width: 0,
                minHeight: 40,
                minWidth: 220,
                isSmallDeviceLocal: isSmallDevice()
            };
        },
        mounted() {
            this.$_subscribeToWindowsMessages();
            this.$_onResizeWindow();
        },
        destroyed() {
            this.$_unsubscribeToWindowsMessages();
        },
        watch: {
            getDetailInformationPanelHeight(newVal) {
                this.$_onResizeWindow();
            },
            getDetailInformationPanelWidth(newVal) {
                this.$_onResizeWindow();
            },
            displayPanel(newVal) {
                if (newVal) {
                    this.$store.dispatch('togglePanel', {
                        panelName: INTERFACE_PANEL_TRIGGERS.INFORMATION,
                        visible: true
                    });
                } else {
                    this.$store.dispatch('togglePanel', {
                        panelName: INTERFACE_PANEL_TRIGGERS.INFORMATION,
                        visible: false
                    });
                }
                this.$_onResizeWindow();
            },
            getSelectionData() {
                this.$store.dispatch('togglePanel', {
                    panelName: INTERFACE_PANEL_TRIGGERS.OBJECT_DETAILS,
                    visible: false
                });

                // ZoomToFeatures after click in map to move map when selection is behind informationpanel
                if (
                    this.getSelectionData &&
                    this.getSelectionData.type === 'layers' &&
                    this.hasResults
                ) {
                    this.zoomToFeatures({
                        features: this.getSelectionGroups[0].features,
                        zoomWhenOutOfView: true
                    });
                }
            },
            groups(newval) {
                // If no groups cleanup some stuff.
                if (newval.length === 0 && this.$refs.informationtabs) {
                    // clear selected items in map
                    this.$refs.informationtabs.$_hideDetails();
                    // clear map countour.
                    this.MAP_ADD_DRAWING({ mapName: 'main' });
                }
                if (this.limitedResultsGroups.length > 0) {
                    this.$store.commit('ADD_STATUS_MESSAGE', {
                        message: `${this.$t(
                            'maxResultsExceeded'
                        )} \n- ${this.limitedResultsGroups.join('\n- ')}`,
                        variant: 'danger',
                        autoHideDelay: 5000
                    });
                    return;
                }

                // display message if:
                // - a selection has been made
                // - it is not from a click on the map (type 'layers')
                // - there are no layers (groups) or there are groups, but they do not have results
                // - it is not after all features are deleted
                const displayNoResultMessage =
                    this.getSelectionData &&
                    this.getSelectionData.type !== 'layers' &&
                    !this.hasResults &&
                    this.getSelectionData.deletedState === false;

                if (displayNoResultMessage && !this.pointer) {
                    this.$store.commit('ADD_STATUS_MESSAGE', {
                        message: `${this.$t('noResultsFoundInLayers')}`,
                        variant: 'warning',
                        autoHideDelay: 5000
                    });
                }
            },
            isLeftPanelVisible() {
                this.$_onResizeWindow();
            },
            'userMapsPanel.width': function (newVal) {
                // Als informationPanel is maximized and
                // size of UserMaps is changed to smaller:
                // maximize informationPanel again. Here is done
                // by resetting maximizeHorizontal and is set in
                // $_setMaximizeHorizontal() again
                if (this.maximizeHorizontal) {
                    const maximizeHorizontal = !this.maximizeHorizontal;
                    const prevWidth = window.innerWidth - this.userMapsPanel.width;
                    this.$_updateInformationPanel(
                        this.height,
                        prevWidth,
                        maximizeHorizontal
                    );
                    this.$_setMaximizeHorizontal();
                }
            },
            maximizeHorizontal(newVal) {
                this.handlers = newVal ? ['t'] : ['t', 'l', 'lt'];
            }
        },
        computed: {
            ...mapState([
                'uiElements',
                'userData',
                'defaultMap',
                'pointer',
                'tabObjectSearch',
                'objectSearchResults'
            ]),
            ...mapGetters([
                'getSelectionData',
                'getSelectionGroups',
                'getSelectionObjectsCount',
                'getSelectionObjects',
                'getSelectionLimitedResultsGroups',
                'getSelectionObjectsSelectedItem',
                'getPanelGroup',
                'isLeftPanelVisible',
                'getDetailInformationPanelHeight',
                'getDetailInformationPanelWidth',
                'getShowTableViewSmallDevices',
                'getOrderedTabs'
            ]),
            informationPanel: function () {
                return this.uiElements[INTERFACE_PANEL_TRIGGERS.INFORMATION];
            },
            userMapsPanel: function () {
                return this.uiElements[INTERFACE_PANEL_TRIGGERS.USERMAPS];
            },
            displayPanel() {
                return (
                    this.informationPanel.focus &&
                    (this.tabObjectSearch || this.pointer || this.groups.length > 0)
                );
            },
            hidePanel() {
                return !this.informationPanel.visible;
            },
            hasRefesh() {
                return !!this.getSelectionData?.refresh;
            },
            hasResults() {
                return (
                    this.groups.length > 0 &&
                    this.groups.find(({ count }) => count > 0)
                );
            },
            hasTabs() {
                return this.getOrderedTabs.length > 0;
            },
            limitedResultsGroups() {
                return this.getSelectionLimitedResultsGroups;
            },
            groups() {
                return this.getSelectionGroups;
            },
            objectList() {
                return this.getSelectionObjects;
            },
            resultCount() {
                const objectCount = this.objectSearchResults.reduce(
                    (sum, item) => sum + item.count,
                    0
                );
                return this.getSelectionObjectsCount + (objectCount ?? 0);
            },
            maxWidth() {
                return this.isLeftPanelVisible
                    ? window.innerWidth - this.leftPosition(true)
                    : window.innerWidth;
            },
            maxHeight() {
                return this.isDetailPanelVisible && this.maximizeHorizontal
                    ? this.maxHeightWhenDetailPanelOpen
                    : window.innerHeight - MENUBAR_HEIGHT;
            },
            maxHeightWhenDetailPanelOpen() {
                const percViewPortHeight = 75;
                const screenHeight = window.innerHeight - MENUBAR_HEIGHT;
                return (screenHeight * percViewPortHeight) / 100;
            },
            maximizeHorizontal() {
                return this.informationPanel.maxWidth;
            },
            prevHeight() {
                const height = this.informationPanel.height;
                return height < this.maxHeight ? height : this.maxHeight;
            },
            prevWidth() {
                const width = this.informationPanel.width;
                return width < this.maxWidth ? width : this.maxWidth;
            },
            isDetailPanelVisible() {
                return this.informationPanel.components.detailPanel.visible;
            },
            showTableViewSmallDevices() {
                return this.getShowTableViewSmallDevices;
            }
        },
        methods: {
            ...mapActions(['zoomToFeatures']),
            ...mapMutations(['MAP_ADD_DRAWING']),
            $_openBrkModal(settings) {
                this.$store.dispatch('togglePanel', {
                    panelName: INTERFACE_PANEL_TRIGGERS.BRK,
                    visible: true,
                    data: settings
                });
            },
            $_openStufDetails(settings) {
                this.$store.dispatch('getNaturalPersons', {
                    name: 'GetNaturalPersons',
                    data: createStufBgQueryObject(settings?.addressdata),
                    title: this.$t('stuf.bewoners')
                });
            },
            $_openKvkDetails(settings) {
                this.$store.dispatch('getKvkCompanies', {
                    name: 'GetHandelsregisterData',
                    data: createKvkQueryObject(settings?.addressdata),
                    title: this.$t('kvk.companies')
                });
            },
            async $_openEnergyLabel(settings) {
                if (settings?.addressdata) {
                    const data = {
                        Postcode: settings?.addressdata.postCode,
                        Huisnummer: settings?.addressdata.huisNummer,
                        VolledigAdres: settings?.addressdata.displayName
                    };

                    this.$store.dispatch('togglePanel', {
                        panelName: INTERFACE_PANEL_TRIGGERS.ENERGYLABEL,
                        visible: true,
                        data: data
                    });
                }
            },
            $_openWozData(settings) {
                if (settings?.addressdata) {
                    // Add property to object only if value is defined
                    const data = {
                        fields: WOZ_FIELDS,
                        id: settings?.wozApiId,
                        ...(settings?.addressdata.huisLetter && {
                            huisletter: settings?.addressdata.huisLetter
                        }),
                        ...(settings?.addressdata.huisNummer && {
                            huisnummer: settings?.addressdata.huisNummer
                        }),
                        ...(settings?.addressdata.huisNummerToevoeging && {
                            huisnummertoevoeging:
                                settings?.addressdata.huisNummerToevoeging
                        }),
                        ...(settings?.addressdata.postCode && {
                            postcode: settings?.addressdata.postCode
                        })
                    };

                    this.$store.dispatch('getWozData', {
                        data,
                        title: settings?.title
                    });
                }
            },
            $_setMaximizeHorizontal() {
                const maximizeHorizontal = !this.maximizeHorizontal;
                const height =
                    this.isDetailPanelVisible && maximizeHorizontal
                        ? this.maxHeightWhenDetailPanelOpen
                        : this.height;
                this.$_updateInformationPanel(
                    height,
                    this.prevWidth,
                    maximizeHorizontal
                );
                this.$_saveUiElementsToDevice();
                this.$_onResizeWindow();
            },
            leftPosition(useDetailPanel = false) {
                if (this.isLeftPanelVisible) {
                    let leftPanel = this.getPanelGroup('left').find(
                        ([, value]) => value.visible
                    );
                    leftPanel = leftPanel ? leftPanel[1] : null;

                    if (useDetailPanel) {
                        const detailPanel = this.isDetailPanelVisible
                            ? this.informationPanel.components.detailPanel
                            : null;
                        leftPanel = leftPanel || detailPanel;
                    }

                    if (leftPanel?.width) {
                        return leftPanel.width;
                    }
                }
                return 0;
            },
            $_subscribeToWindowsMessages() {
                window.addEventListener('resize', this.$_onResizeWindow);
            },
            $_unsubscribeToWindowsMessages() {
                window.removeEventListener('resize', this.$_onResizeWindow);
            },
            $_openDetails() {
                if (
                    this.isDetailPanelVisible &&
                    this.maximizeHorizontal &&
                    this.height > this.maxHeightWhenDetailPanelOpen
                ) {
                    this.$_updateInformationPanel(
                        this.maxHeightWhenDetailPanelOpen,
                        this.prevWidth,
                        this.maximizeHorizontal
                    );
                    this.$_saveUiElementsToDevice();
                    this.$_onResizeWindow();
                }
            },
            $_updateInformationPanel(height, width, maxWidth) {
                this.$store.commit('UPDATE_INFORMATION_PANEL', {
                    height,
                    width,
                    maxWidth
                });
            },
            $_saveUiElementsToDevice() {
                if (this.$store.getters.isMobile) {
                    this.$store.commit('SAVE_UIELEMENTS_STATE_TO_DEVICE');
                }
            },
            resizeHandler(data) {
                this.$_updateInformationPanel(
                    data.height,
                    data.width,
                    this.maximizeHorizontal
                );
                this.$_saveUiElementsToDevice();
                this.$_onResizeWindow();
            },
            $_onResizeWindow() {
                this.isSmallDeviceLocal = isSmallDevice();

                const percViewPortWidth = 50;
                const screenWidth = window.innerWidth;
                const screenHeight = window.innerHeight;
                const maxWidthOnLargeDevice =
                    this.maximizeHorizontal && !this.isSmallDeviceLocal;
                const contextMenuOnSmallDevice =
                    this.pointer && this.isSmallDeviceLocal;

                if (this.displayPanel) {
                    this.height = this.prevHeight;
                    this.top = screenHeight - this.height;
                } else {
                    this.height = 0;
                    this.top = screenHeight;
                }

                if (maxWidthOnLargeDevice || contextMenuOnSmallDevice) {
                    this.left = this.leftPosition();
                    this.width = screenWidth - this.left;
                    this.$store.commit('UPDATE_STYLE_PANELS', this.height !== 0);
                } else if (isSmallDevice() && this.$store.getters.isWeb) {
                    // Maximize panel width on small devices
                    this.width = screenWidth;
                    this.left = 0;
                    this.$store.commit('UPDATE_STYLE_PANELS', this.height !== 0);
                } else {
                    this.width =
                        this.prevWidth ?? (screenWidth * percViewPortWidth) / 100;
                    this.left = screenWidth - this.width;
                    this.$store.commit('UPDATE_STYLE_PANELS', false);
                }
            }
        }
    };
</script>

<style>
.information-panel .nav-tabs .nav-link.close-tab {
    padding-right: 0;
}
</style>

<style scoped lang="scss">
.resizable {
    @include z-index(information-panel);
    position: absolute !important;

    &.hidden {
        display: none;
    }
}

.information-panel {
    &:not(.accessible) {
        width: 100%;
        height: 100%;
        background-color: #ffffff;
        -moz-transition: all 0.3s ease-in-out;
        -o-transition: all 0.3s ease-in-out;
        transition: all 0.3s ease-in-out;
        flex-direction: column;
    }

    .arrow-min-max {
        width: 30px;
        height: 30px;
        cursor: pointer;
        background-color: #ffffff;
        mask-position: center;
        mask-size: 24px;

        &.max {
            mask-image: url('~@/assets/imported-icons/ic_fluent_chevron_double_left_20_regular.svg');
        }
        &.min {
            mask-image: url('~@/assets/imported-icons/ic_fluent_chevron_double_right_20_regular.svg');
        }
    }

    .up-white-icon {
        width: 30px;
        height: 30px;
        background: url('~@/assets/icons/ic_fluent_chevron_up_20_white.svg')
            no-repeat center center;
        background-size: 22px;
        cursor: pointer;
        text-indent: 999em;
        overflow: hidden;

        &.disabled {
            display: none;
        }
    }

    .rotate_180 {
        transform: rotate(180deg);
        -webkit-transform: rotate(180deg); /* Chrome, Safari, Opera */
    }

    .object-overview-button {
        position: absolute;
        right: 0px;
        top: -40px;
        min-width: 220px;
        height: 40px;
        line-height: 30px;
        padding: 4px 8px;
        color: #fff;
        background-color: #007ac9;
        z-index: 3;
        word-wrap: initial;

        &.disabled {
            color: $gray-04;
            background-color: $gray-02;
        }
    }

    .information-tab-content {
        height: 100%;
    }

    .tab-wait {
        padding: 5px;
    }
}
@media (max-width: 767px) {
    .large-only:not(.small-device) {
        display: none;
    }
    .resizable {
        &.show {
            &:not(.has-context-tab):not(.small-device) {
                /* use !important to overrule resizable default settings */
                width: 0 !important;
                height: 0 !important;
                top: 100vh !important;
                left: 100vw !important;
            }
        }
    }
}
</style>
